import * as React from "react";
import {InView} from "react-intersection-observer";

import {css} from "/src/utils/utils";

export enum TRANSITION_BLOCK_FROM {
  top = "top",
  right = "right",
  bottom = "bottom",
  left = "left",
}

export interface TransitionBlockProps {
  children?: string | React.ReactElement | React.ReactNode;
  from?: TRANSITION_BLOCK_FROM;
  classes?: string;
  delay?: number;
  transition?: string;
  offset?: number;
  duration?: number;
  animateOpacity?: boolean;
  triggerOnce?: boolean;
  threshold?: number;
  display?: "block" | "inline-block" | "flex" | "grid" | "none";
  callback?: () => void;
  onTransitionEnd?: React.TransitionEventHandler<HTMLDivElement>;
}

const TransitionBlock = (props: TransitionBlockProps): React.ReactElement => {
  const [playAnimation, setPlayAnimation] = React.useState<boolean>(false);
  const elRef = React.useRef<HTMLDivElement | null>(null);

  const {
    children,
    from = TRANSITION_BLOCK_FROM.bottom,
    delay = 0,
    transition = "ease-out",
    duration = 1250,
    offset = 100,
    classes,
    animateOpacity = true,
    triggerOnce = false,
    threshold = 0,
    display = "inline-block",
    callback,
    onTransitionEnd,
  } = props;

  const onChange = (inView: boolean) => {
    setPlayAnimation(inView);

    if (callback) {
      callback();
    }
  };

  const getStyles = () => {
    const offsetPx = `${offset}px`;

    let transform = `translate3d(0, ${offsetPx}, 0)`;

    if (playAnimation) {
      transform = "translate3d(0, 0, 0)";
    } else {
      switch (from) {
        case TRANSITION_BLOCK_FROM.top:
          transform = `translate3d(0, -${offsetPx}, 0)`;
          break;
        case TRANSITION_BLOCK_FROM.right:
          transform = `translate3d(${offsetPx}, 0, 0)`;
          break;
        case TRANSITION_BLOCK_FROM.bottom:
          transform = `translate3d(0, ${offsetPx}, 0)`;
          break;
        case TRANSITION_BLOCK_FROM.left:
          transform = `translate3d(-${offsetPx}, 0, 0)`;
          break;
        default:
          break;
      }
    }

    return {
      display,
      opacity: playAnimation || !animateOpacity ? 1 : 0,
      width: "100%",
      transition: `opacity ${duration}ms ease ${delay}ms, transform ${duration}ms ${transition} ${delay}ms`,
      transform,
    };
  };

  return (
    <InView
      triggerOnce={triggerOnce}
      className={css(classes!)}
      onChange={onChange}
      threshold={threshold}
    >
      <div ref={elRef}>
        <div onTransitionEnd={onTransitionEnd} style={getStyles()}>
          {children}
        </div>
      </div>
    </InView>
  );
};

export default TransitionBlock;
